import React, { useState } from "react";
import {
  BASE_PATH,
  BuildingOUT,
  getLocalAuthHeader,
  getLocalAuthToken,
  ifcApi,
  MaterialResourceOUT,
} from "api-client";
import { DownloadIcon } from "@heroicons/react/solid";
import { DocumentTextIcon, TrashIcon } from "@heroicons/react/outline";
import useToast from "hooks/useToast";
import ActionMenu from "components/ActionMenu";
import Modal from "components/Modal";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router";
import { Icon3dCubeSphere } from "@tabler/icons-react";
import { buildingStore } from "store/Building/BuildingStore";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { ROUTES_ENUM } from "routes/consts";
import clsx from "clsx";
import useFeatureFlag from "hooks/useFeatureFlag";

interface SourceFilesActionsProps {
  file: MaterialResourceOUT;
  openSettings?: () => void;
  disabled: boolean;
}

export const SourceFilesActions = observer(
  ({ file, disabled = false }: SourceFilesActionsProps) => {
    const toast = useToast();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isFeatureVisible } = useFeatureFlag();
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [reportContent, setReportContent] = useState<string>("");
    const [isReportLoading, setIsReportLoading] = useState(false);

    const openProcessingReport = () => {
      setIsReportOpen(true);
      setIsReportLoading(true);
      setReportContent("");
      ifcApi
        .ifcApiV1RoutersIfcFileGetProcessingReport(
          file.id,
          getLocalAuthHeader()
        )
        .then((response) => {
          setReportContent(response.data.content ?? "");
        })
        .catch((err) => {
          toast(err.message, "warning");
          setIsReportOpen(false);
        })
        .finally(() => setIsReportLoading(false));
    };

    const deleteFiles = () => {
      const authHeader = getLocalAuthHeader();
      ifcApi
        .ifcApiV1RoutersIfcFileDeleteIfcFile(file.id, authHeader)
        .then(async () => {
          await buildingStore.getAllSourceFiles();
          await buildingStore
            .getCurrentBuildingDetails()
            .then((response: void | BuildingOUT) => {
              response && buildingStore.setCurrentBuilding(response);
            });
          toast(t("buildingMaterialResources.deleteFileSuccess"), "success");
        })
        .catch((err) => toast(err.message, "warning"));
    };

    const goTo3DViewer = () =>
      navigate(`../${ROUTES_ENUM.VIEWER}/${file.id}`, {
        state: `${file.name}.${file.file_type ?? ""}`,
      });

    const downloadWithMappings = () => {
      const token = getLocalAuthToken();
      const url = `${BASE_PATH}/api/v1/ifc/files/${file.id}/download_with_mappings?bearer=${token}`;
      window.open(url, "_blank");
    };

    const menuItems = [
      ...(file.file_processed_xkt && !disabled
        ? [
            <div
              key={0}
              onClick={goTo3DViewer}
              className="flex items-center px-4 py-1 w-full"
            >
              <Icon3dCubeSphere width={15} className="mr-3" />
              {t("buildingMaterialResources.openIn3DViewer")}
            </div>,
          ]
        : []),
      ...(file.has_processing_report
        ? [
            <div
              key={3}
              onClick={openProcessingReport}
              className="flex items-center px-4 py-1.5 w-full"
            >
              <DocumentTextIcon width={15} className="mr-3" />
              {t("buildingMaterialResources.seeProcessingReport")}
            </div>,
          ]
        : []),
      <a
        key={1}
        href={file.file as string}
        className="flex items-center px-4 py-1.5 w-full"
        download
      >
        <DownloadIcon width={15} className="mr-3" />
        {t("buildingMaterialResources.download")}
      </a>,
      <a
        key={34}
        onClick={downloadWithMappings}
        className={clsx(
          "flex items-center px-4 py-1.5 w-full whitespace-nowrap",
          {
            hidden:
              file.file_type === "IFC" ||
              !isFeatureVisible("download_with_mappings"),
          }
        )}
        download
      >
        <DownloadIcon width={15} className="mr-3" />
        {t("buildingMaterialResources.downloadWithMappings")}
      </a>,
      <div
        key={2}
        onClick={deleteFiles}
        className={clsx("flex items-center px-4 py-1.5 w-full", {
          "cursor-not-allowed pointer-events-none opacity-40":
            buildingStore.currentBuilding?.is_demo,
        })}
      >
        <TrashIcon width={15} className="mr-3" />
        {t("buildingMaterialResources.delete")}
      </div>,
    ];

    return (
      <>
        <ActionMenu
          dotSize={18}
          className="z-10 "
          items={menuItems}
          direction="bottom"
          itemsClassName="!-top-2 !right-3"
          itemClassName="!p-0 min-w-[200px]"
        />
        <Modal
          isOpen={isReportOpen}
          setIsOpen={setIsReportOpen}
          closeButton
          scrollable
          title={t("buildingMaterialResources.processingReport")}
          containerClassName="sm:my-8 sm:max-w-sm sm:w-full md:max-w-3xl"
        >
          {isReportLoading ? (
            <div className="px-6 py-8 text-center text-gray-500">
              {t("commons.loading")}
            </div>
          ) : (
            <ReactMarkdown
              className="markdown px-6 py-4 prose max-w-none"
              remarkPlugins={[remarkGfm]}
            >
              {reportContent}
            </ReactMarkdown>
          )}
        </Modal>
      </>
    );
  }
);
